import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { Box, Stack, Tooltip, Typography } from '@mui/material'

import { useUser } from 'store'
import { FormSelect, getSsidPlaceholder } from '../../Form/StimeFields'
import {
  TemplatePreviewTrigger,
  dropZoneIconSx,
  dropZoneSx,
  templateTooltipArrowSx,
  templateTooltipSx
} from './ImportStyles'
import TemplatePreview from './TemplatePreview'
import { getTemplate } from './csv'
import { canSeeStimeFields } from '../../permissions'

const StepFile = ({ ssidOptions, ssidId, withPsk, onSsidChange, onChange, fileError }) => {
  const [file, setFile] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const { t } = useTranslation()
  const [user] = useUser()
  const { isStime } = useOutletContext()
  const handleFile = async selectedFile => {
    if (!selectedFile) return

    const content = await selectedFile.text()
    setFile(selectedFile)
    onChange(selectedFile, content)
  }

  const handleFileChange = event => {
    handleFile(event.target.files?.[0])
  }

  const handleDrop = event => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    handleFile(event.dataTransfer.files?.[0])
  }

  const hasFileError = Boolean(fileError)
  const isSsidMissing = Boolean(isStime) && canSeeStimeFields(user) && !ssidId

  const handleTemplateTriggerKeyDown = event => {
    if (event.key !== 'Enter' || isSsidMissing) {
      return
    }
    const link = document.createElement('a')
    link.href = getTemplate(withPsk).url
    link.download = ''
    link.click()
  }

  const getDropZoneIcon = () => {
    if (hasFileError) {
      return 'triangle-exclamation'
    }
    return file ? 'file-csv' : 'cloud-arrow-up'
  }

  return (
    <Stack sx={{ gap: 2.5 }}>
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2
        }}>
        <Typography sx={{ maxWidth: 370, fontSize: 12, lineHeight: 1.75, color: '#7A8096' }}>
          {t('ran.import.description.content')}
        </Typography>
        <Tooltip
          title={<TemplatePreview withPsk={withPsk} isSsidMissing={isSsidMissing} />}
          placement="bottom-end"
          enterDelay={150}
          arrow
          slotProps={{ tooltip: { sx: templateTooltipSx }, arrow: { sx: templateTooltipArrowSx } }}>
          <TemplatePreviewTrigger component="span" tabIndex={0} onKeyDown={handleTemplateTriggerKeyDown}>
            <i className="fa-regular fa-eye" aria-hidden="true" />
            {t('ran.actions.download_template')}
          </TemplatePreviewTrigger>
        </Tooltip>
      </Stack>
      {isStime && canSeeStimeFields(user) && (
        <FormSelect
          id="ran-import-ssid"
          label={t('ran.form.ssid', 'SSID')}
          value={ssidId}
          options={ssidOptions}
          placeholder={getSsidPlaceholder(t, ssidOptions)}
          disabled={ssidOptions.length === 0}
          onChange={onSsidChange}
        />
      )}
      <Box
        component="label"
        htmlFor="ran-import-file"
        sx={dropZoneSx(Boolean(file), isDragging, hasFileError)}
        onDragEnter={event => {
          event.preventDefault()
          setIsDragging(true)
        }}
        onDragOver={event => {
          event.preventDefault()
          event.dataTransfer.dropEffect = 'copy'
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}>
        <input id="ran-import-file" type="file" hidden accept=".csv" onChange={handleFileChange} />
        <Stack sx={{ alignItems: 'center', gap: 0.75, maxWidth: 420, paddingX: 2, textAlign: 'center' }}>
          <Box sx={dropZoneIconSx(Boolean(file), hasFileError)}>
            <i className={`fa-regular fa-${getDropZoneIcon()}`} />
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#292929' }}>
            {file ? file.name : t('ran.import.dropzone_title')}
          </Typography>
          {hasFileError ? (
            <Typography role="alert" sx={{ fontSize: 11, lineHeight: 1.5, color: 'error.main' }}>
              {fileError}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 11, color: '#A5A9B3' }}>
              {file ? t('ran.import.file_to_import') : t('ran.import.dropzone_subtitle')}
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  )
}

export default StepFile
